import { useState, useRef } from "react";
import { toast } from "sonner";
import { http, formatError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { IdentificationCard, UploadSimple, CheckCircle, Clock, XCircle } from "@phosphor-icons/react";

const ID_TYPES = ["National ID (NIN)", "Driver's Licence", "International Passport", "Voter's Card"];

export default function KycUpload({ kyc, onDone }) {
  const [idType, setIdType] = useState(kyc?.submission?.id_type || "");
  const [idNumber, setIdNumber] = useState(kyc?.submission?.id_number || "");
  const [idFile, setIdFile] = useState(null);
  const [selfieFile, setSelfieFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const idRef = useRef(); const selfRef = useRef();

  const status = kyc?.kyc_status || "pending";
  const subStatus = kyc?.submission?.status;
  const canResubmit = subStatus === "rejected" || !kyc?.submission;

  const fileToB64 = (file) => new Promise((res, rej) => {
    const r = new FileReader();
    r.onload = () => res(r.result);
    r.onerror = rej;
    r.readAsDataURL(file);
  });

  const submit = async (e) => {
    e.preventDefault();
    if (!idFile) { toast.error("Please upload a photo of your ID."); return; }
    if (idFile.size > 3 * 1024 * 1024) { toast.error("ID document too large (max 3MB)"); return; }
    if (selfieFile && selfieFile.size > 2 * 1024 * 1024) { toast.error("Selfie too large (max 2MB)"); return; }
    setLoading(true);
    try {
      const id_document_b64 = await fileToB64(idFile);
      const selfie_b64 = selfieFile ? await fileToB64(selfieFile) : "";
      const { data } = await http.post("/kyc/submit", { id_type: idType, id_number: idNumber, id_document_b64, selfie_b64 });
      toast.success(data?.message || "KYC submitted");
      setIdFile(null); setSelfieFile(null);
      if (idRef.current) idRef.current.value = "";
      if (selfRef.current) selfRef.current.value = "";
      onDone?.();
    } catch (err) { toast.error(formatError(err)); }
    finally { setLoading(false); }
  };

  const StatusPill = () => {
    if (status === "verified") return <Badge className="bg-emerald-100 text-emerald-800 hover:bg-emerald-100"><CheckCircle size={12} className="mr-1" weight="bold" />Verified</Badge>;
    if (subStatus === "rejected") return <Badge className="bg-red-100 text-red-800 hover:bg-red-100"><XCircle size={12} className="mr-1" weight="bold" />Rejected</Badge>;
    if (kyc?.submission) return <Badge className="bg-amber-100 text-amber-800 hover:bg-amber-100"><Clock size={12} className="mr-1" weight="bold" />Under Review</Badge>;
    return <Badge className="bg-slate-100 text-slate-700 hover:bg-slate-100">Not Submitted</Badge>;
  };

  return (
    <div className="bg-white border border-slate-200 rounded-2xl p-8 max-w-3xl">
      <div className="flex items-center gap-3 mb-6">
        <div className="h-12 w-12 rounded-2xl bg-[#0a1128] grid place-items-center"><IdentificationCard size={22} className="text-[#00b4d8]" weight="bold" /></div>
        <div>
          <p className="font-display font-bold text-xl text-[#0a1128]">KYC Verification</p>
          <p className="text-sm text-slate-500">Verify your identity to unlock higher investment limits.</p>
        </div>
        <div className="ml-auto"><StatusPill /></div>
      </div>

      {status === "verified" ? (
        <div className="rounded-2xl bg-emerald-50 border border-emerald-200 p-6 flex gap-3">
          <CheckCircle size={22} className="text-emerald-700 mt-0.5" weight="fill" />
          <div>
            <p className="font-display font-bold text-emerald-900">You are verified.</p>
            <p className="text-sm text-emerald-800 mt-1">Your KYC is complete. You can participate in all Mayoto opportunities.</p>
          </div>
        </div>
      ) : (
        <>
          {kyc?.submission && subStatus === "pending" && (
            <div className="rounded-2xl bg-amber-50 border border-amber-200 p-4 mb-6 text-sm text-amber-900">
              Your KYC is submitted. Our team will review within 24-48 hours.
            </div>
          )}
          {subStatus === "rejected" && kyc?.submission?.notes && (
            <div className="rounded-2xl bg-red-50 border border-red-200 p-4 mb-6 text-sm text-red-900">
              <span className="font-semibold">Rejected:</span> {kyc.submission.notes}. Please resubmit.
            </div>
          )}

          {canResubmit && (
            <form onSubmit={submit} className="grid sm:grid-cols-2 gap-5" data-testid="kyc-form">
              <div>
                <Label>Identification Type</Label>
                <Select value={idType} onValueChange={setIdType}>
                  <SelectTrigger data-testid="kyc-id-type"><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent className="bg-white">{ID_TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>ID Number</Label><Input required value={idNumber} onChange={e => setIdNumber(e.target.value)} data-testid="kyc-id-number" /></div>
              <div className="sm:col-span-2">
                <Label>Upload ID document (photo of front) — max 3MB</Label>
                <div className="mt-1 border border-dashed border-slate-300 rounded-xl p-6 text-center">
                  <input ref={idRef} type="file" accept="image/*,application/pdf" onChange={e => setIdFile(e.target.files?.[0])} className="hidden" id="kyc-id-file" data-testid="kyc-id-file" />
                  <label htmlFor="kyc-id-file" className="cursor-pointer inline-flex items-center gap-2 text-sm font-semibold text-[#00b4d8]"><UploadSimple size={18} />{idFile ? idFile.name : "Choose file"}</label>
                </div>
              </div>
              <div className="sm:col-span-2">
                <Label>Selfie (optional) — max 2MB</Label>
                <div className="mt-1 border border-dashed border-slate-300 rounded-xl p-6 text-center">
                  <input ref={selfRef} type="file" accept="image/*" onChange={e => setSelfieFile(e.target.files?.[0])} className="hidden" id="kyc-selfie" data-testid="kyc-selfie" />
                  <label htmlFor="kyc-selfie" className="cursor-pointer inline-flex items-center gap-2 text-sm font-semibold text-[#00b4d8]"><UploadSimple size={18} />{selfieFile ? selfieFile.name : "Choose file"}</label>
                </div>
              </div>
              <div className="sm:col-span-2">
                <Button type="submit" disabled={loading || !idType} className="btn-shine bg-[#00b4d8] hover:bg-[#0096c7] rounded-full px-7 py-6 text-base w-full sm:w-auto" data-testid="kyc-submit">
                  {loading ? "Submitting…" : "Submit KYC"}
                </Button>
              </div>
              <p className="sm:col-span-2 text-xs text-slate-500">Documents are stored securely and used only for identity verification. Never share your bank PIN or password.</p>
            </form>
          )}
        </>
      )}
    </div>
  );
}
